import React, { useState, useMemo } from 'react';
import {
  Filter,
  GraduationCap,
  Users,
  CheckCircle2,
  Clock,
  BookOpen,
  Eye,
  X,
  UserCheck,
  ShieldCheck,
  RotateCcw,
} from 'lucide-react';
import { AdminStudent, AdminTeacher, AcademicLevel } from '../types';
import { Badge, Button, Search } from '../../components/core';

type StudentProgressStatus = 'Registration' | 'Level Assigned' | 'Learning in Progress' | 'Teacher Review' | 'Final Revision' | 'Approved' | 'Promoted';

interface StudentProgressProps {
  students: AdminStudent[];
  teachers: AdminTeacher[];
  levels: AcademicLevel[];
}

const getStatusBadgeVariant = (
  status: string
): 'success' | 'warning' | 'soft' | 'accent' | 'outline' => {
  switch (status) {
    case 'Approved':
    case 'Promoted':
      return 'success';
    case 'Teacher Review':
    case 'Final Revision':
      return 'warning';
    case 'Learning in Progress':
      return 'soft';
    case 'Level Assigned':
      return 'accent';
    case 'Registration':
      return 'outline';
    default:
      return 'outline';
  }
};

export const StudentProgress: React.FC<StudentProgressProps> = ({
  students,
  teachers,
  levels,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [teacherFilter, setTeacherFilter] = useState('ALL');

  const [selectedStudent, setSelectedStudent] = useState<AdminStudent | null>(null);

  // Helper map to assign mock rich progress details consistently based on student ID
  const getStudentProgressData = (student: AdminStudent) => {
    // Determine assigned teacher
    const teacherForLevel = teachers.find((t) => t.assignedLevels.some((l) => student.level.includes(l))) || teachers[0];
    const assignedTeacherName = teacherForLevel ? teacherForLevel.name : 'Rajvi Ben Shah';

    // Map level to assigned topics
    const levelObj = levels.find((l) => student.level.includes(l.name) || l.name.includes(student.level.split(' - ')[0])) || levels[0];
    const levelSyllabus = levelObj?.syllabus || [
      { id: '1', levelId: '1', chapterName: 'Navkar Mantra - Part 1', learningType: 'Sutra', displayOrder: 1, status: 'Active' },
      { id: '2', levelId: '1', chapterName: 'Chattari Mangalam - Recitation', learningType: 'Sutra', displayOrder: 2, status: 'Active' },
      { id: '3', levelId: '1', chapterName: 'Story of King Kumarapala', learningType: 'Story', displayOrder: 3, status: 'Active' },
      { id: '4', levelId: '1', chapterName: 'Ichhami Khamasamano', learningType: 'Sutra', displayOrder: 4, status: 'Active' },
    ];

    // Total topics
    const totalTopicsCount = levelSyllabus.length > 0 ? levelSyllabus.length : 12;

    // Derived completed topics based on student totalGathas or index
    let completedRatio = 0.5;
    if (student.attendanceRate > 90) completedRatio = 0.8;
    else if (student.attendanceRate > 80) completedRatio = 0.65;
    else if (student.attendanceRate > 70) completedRatio = 0.45;
    else completedRatio = 0.3;

    const completedTopicsCount = Math.min(totalTopicsCount, Math.max(1, Math.round(totalTopicsCount * completedRatio)));
    const pendingTopicsCount = Math.max(0, totalTopicsCount - completedTopicsCount);
    const progressPct = Math.round((completedTopicsCount / totalTopicsCount) * 100);

    const completedTopics = levelSyllabus.slice(0, completedTopicsCount);
    const pendingTopics = levelSyllabus.slice(completedTopicsCount);

    // Derived Status for Monitoring
    let currentStatus: 'Registration' | 'Level Assigned' | 'Learning in Progress' | 'Teacher Review' | 'Final Revision' | 'Approved' | 'Promoted' = 'Learning in Progress';
    let teacherReviewStatus = 'Pending Guruji Review';
    let finalRevisionStatus = 'Not Required Yet';
    let promotionStatus = 'In Progress';
    let currentSubmissionStatus = `Submitted Chapter ${completedTopicsCount} Gatha`;

    if (progressPct >= 90) {
      currentStatus = 'Approved';
      teacherReviewStatus = 'Approved by Guruji';
      finalRevisionStatus = 'Completed';
      promotionStatus = 'Eligible for Next Level';
      currentSubmissionStatus = 'All Level Requirements Met';
    } else if (progressPct >= 75) {
      currentStatus = 'Teacher Review';
      teacherReviewStatus = 'Under Evaluation';
      finalRevisionStatus = 'Pending Final Oral Test';
      promotionStatus = 'Pending Final Assessment';
      currentSubmissionStatus = `Chapter ${completedTopicsCount} Audio Under Review`;
    } else if (progressPct >= 50) {
      currentStatus = 'Learning in Progress';
      teacherReviewStatus = 'Periodic Checks OK';
      finalRevisionStatus = 'In Progress';
      promotionStatus = 'In Progress';
      currentSubmissionStatus = `Chapter ${completedTopicsCount} Homework Verified`;
    } else if (progressPct >= 20) {
      currentStatus = 'Level Assigned';
      teacherReviewStatus = 'Initial Orientation Done';
      finalRevisionStatus = 'Not Started';
      promotionStatus = 'In Progress';
      currentSubmissionStatus = 'Basic Sutra Practice';
    }

    const lastActivity = student.registrationDate ? `${student.registrationDate} 10:30 AM` : '2026-07-23 09:15 AM';

    return {
      assignedTeacherName,
      totalTopicsCount,
      completedTopicsCount,
      pendingTopicsCount,
      progressPct,
      completedTopics,
      pendingTopics,
      currentStatus: currentStatus as StudentProgressStatus,
      teacherReviewStatus,
      finalRevisionStatus,
      promotionStatus,
      currentSubmissionStatus,
      lastActivity,
    };
  };

  // Filtered Students
  const filteredStudents = useMemo(() => {
    return students.filter((st) => {
      const progress = getStudentProgressData(st);
      const fullName = `${st.firstName} ${st.surname}`.toLowerCase();
      const matchesSearch =
        fullName.includes(searchQuery.toLowerCase()) ||
        st.mobile.includes(searchQuery) ||
        st.parentMobile.includes(searchQuery) ||
        st.level.toLowerCase().includes(searchQuery.toLowerCase()) ||
        progress.assignedTeacherName.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesLevel = levelFilter === 'ALL' || st.level.includes(levelFilter);
      const matchesStatus = statusFilter === 'ALL' || progress.currentStatus === statusFilter;
      const matchesTeacher = teacherFilter === 'ALL' || progress.assignedTeacherName.includes(teacherFilter);

      return matchesSearch && matchesLevel && matchesStatus && matchesTeacher;
    });
  }, [students, searchQuery, levelFilter, statusFilter, teacherFilter]);

  // Overall Statistics for KPI Row
  const stats = useMemo(() => {
    let learning = 0;
    let review = 0;
    let approved = 0;

    students.forEach((s) => {
      const data = getStudentProgressData(s);
      if (data.currentStatus === 'Approved' || data.currentStatus === 'Promoted') approved++;
      else if (data.currentStatus === 'Teacher Review' || data.currentStatus === 'Final Revision') review++;
      else learning++;
    });

    return {
      total: students.length,
      learning,
      review,
      approved,
    };
  }, [students]);

  const hasActiveFilters =
    searchQuery || levelFilter !== 'ALL' || statusFilter !== 'ALL' || teacherFilter !== 'ALL';

  return (
    <div className="space-y-6">
      {/* Top Banner / Header */}
      <div className="admin-card p-5">
        <h1 className="text-xl font-bold text-[var(--gv-ink)] tracking-tight">Student Progress Monitoring</h1>
      </div>

      {/* KPI Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="admin-card p-4 flex items-center justify-between">
          <div>
            <span className="text-[11px] font-mono font-bold text-[var(--gv-ink-muted)] uppercase">Total Students</span>
            <div className="text-2xl font-bold text-[var(--gv-ink)] mt-0.5">{stats.total}</div>
          </div>
          <div className="p-3 bg-[var(--gv-surface)] rounded-xl text-[var(--gv-ink-muted)]">
            <Users className="w-5 h-5" />
          </div>
        </div>

        <div className="admin-card p-4 flex items-center justify-between">
          <div>
            <span className="text-[11px] font-mono font-bold text-blue-600 uppercase">Learning Active</span>
            <div className="text-2xl font-bold text-[var(--gv-ink)] mt-0.5">{stats.learning}</div>
          </div>
          <div className="p-3 bg-blue-50 rounded-xl text-blue-700">
            <BookOpen className="w-5 h-5" />
          </div>
        </div>

        <div className="admin-card p-4 flex items-center justify-between">
          <div>
            <span className="text-[11px] font-mono font-bold text-amber-600 uppercase">Under Review</span>
            <div className="text-2xl font-bold text-[var(--gv-ink)] mt-0.5">{stats.review}</div>
          </div>
          <div className="p-3 bg-amber-50 rounded-xl text-amber-700">
            <Clock className="w-5 h-5" />
          </div>
        </div>

        <div className="admin-card p-4 flex items-center justify-between">
          <div>
            <span className="text-[11px] font-mono font-bold text-emerald-600 uppercase">Approved / Promoted</span>
            <div className="text-2xl font-bold text-[var(--gv-ink)] mt-0.5">{stats.approved}</div>
          </div>
          <div className="p-3 bg-emerald-50 rounded-xl text-emerald-700">
            <CheckCircle2 className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Search and Filters Controls */}
      <div className="admin-card p-4 space-y-3">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-3">
          <Search
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search student name, mobile, teacher..."
            containerClassName="w-full lg:w-96"
          />

          {/* Filter Selectors */}
          <div className="flex items-center gap-2.5 w-full lg:w-auto overflow-x-auto pb-1 lg:pb-0">
            <div className="flex items-center gap-1.5 text-xs font-bold text-[var(--gv-ink-muted)] shrink-0">
              <Filter className="w-3.5 h-3.5 text-[var(--gv-ink-muted)]" />
              <span>Filters:</span>
            </div>

            {/* Level Filter */}
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="admin-input bg-[var(--gv-surface)] border border-[var(--gv-border)] text-xs font-semibold rounded-xl px-3 py-2 text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none cursor-pointer shrink-0"
            >
              <option value="ALL">All Levels</option>
              {levels.map((l) => (
                <option key={l.id} value={l.name}>
                  {l.name}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="admin-input bg-[var(--gv-surface)] border border-[var(--gv-border)] text-xs font-semibold rounded-xl px-3 py-2 text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none cursor-pointer shrink-0"
            >
              <option value="ALL">All Statuses</option>
              <option value="Learning in Progress">Learning in Progress</option>
              <option value="Teacher Review">Teacher Review</option>
              <option value="Approved">Approved</option>
              <option value="Promoted">Promoted</option>
              <option value="Level Assigned">Level Assigned</option>
            </select>

            {/* Teacher Filter */}
            <select
              value={teacherFilter}
              onChange={(e) => setTeacherFilter(e.target.value)}
              className="admin-input bg-[var(--gv-surface)] border border-[var(--gv-border)] text-xs font-semibold rounded-xl px-3 py-2 text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none cursor-pointer shrink-0"
            >
              <option value="ALL">All Teachers</option>
              {teachers.map((t) => (
                <option key={t.id} value={t.name}>
                  {t.name}
                </option>
              ))}
            </select>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={RotateCcw}
                onClick={() => {
                  setSearchQuery('');
                  setLevelFilter('ALL');
                  setStatusFilter('ALL');
                  setTeacherFilter('ALL');
                }}
              >
                Reset
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Student Progress Table */}
      <div className="admin-card overflow-hidden">
        <div className="p-4 border-b border-[var(--gv-border)] flex items-center justify-between bg-[var(--gv-surface)]/50">
          <span className="text-xs font-bold text-[var(--gv-ink)] font-mono uppercase tracking-wider">
            Monitored Students ({filteredStudents.length})
          </span>
          <span className="text-[11px] text-[var(--gv-ink-muted)] font-medium">
            Showing overall syllabus progress and teacher review states
          </span>
        </div>

        {filteredStudents.length === 0 ? (
          <div className="text-center py-16 bg-[var(--gv-surface)]/30 space-y-3">
            <GraduationCap className="w-10 h-10 text-[var(--gv-ink-muted)] mx-auto" />
            <p className="text-sm font-bold text-[var(--gv-ink)]">No student records found</p>
            <p className="text-xs text-[var(--gv-ink-muted)]">Try adjusting your search or category filters above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead className="bg-stone-100/80 text-[var(--gv-ink-muted)] font-mono text-[10px] uppercase tracking-wider border-b border-[var(--gv-border)]">
                <tr>
                  <th className="py-3 px-4 font-bold">Student Name</th>
                  <th className="py-3 px-4 font-bold">Mobile Number</th>
                  <th className="py-3 px-4 font-bold">Assigned Teacher</th>
                  <th className="py-3 px-4 font-bold">Current Level</th>
                  <th className="py-3 px-4 font-bold">Syllabus Progress</th>
                  <th className="py-3 px-4 font-bold">Current Status</th>
                  <th className="py-3 px-4 font-bold">Last Activity</th>
                  <th className="py-3 px-4 font-bold text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 font-medium text-[var(--gv-ink)] bg-white">
                {filteredStudents.map((st) => {
                  const progress = getStudentProgressData(st);

                  return (
                    <tr key={st.id} className="hover:bg-stone-50/80 transition-colors">
                      {/* Student Name */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={st.photoUrl}
                            alt={`${st.firstName} ${st.surname}`}
                            className="w-9 h-9 rounded-full object-cover border border-[var(--gv-border)] shrink-0"
                          />
                          <div>
                            <div className="font-bold text-[var(--gv-ink)] text-xs">
                              {st.firstName} {st.surname}
                            </div>
                            <span className="text-[10px] font-mono text-[var(--gv-ink-muted)]">{st.id}</span>
                          </div>
                        </div>
                      </td>

                      {/* Mobile Number */}
                      <td className="py-3.5 px-4 font-mono text-[var(--gv-ink-muted)]">
                        <div className="text-xs font-semibold">{st.mobile}</div>
                        <span className="text-[10px] text-[var(--gv-ink-muted)] block">Parent: {st.parentMobile}</span>
                      </td>

                      {/* Assigned Teacher */}
                      <td className="py-3.5 px-4">
                        <div className="font-bold text-[var(--gv-ink)] flex items-center gap-1.5">
                          <UserCheck className="w-3.5 h-3.5 text-[var(--gv-brand)]" />
                          <span>{progress.assignedTeacherName}</span>
                        </div>
                      </td>

                      {/* Current Level */}
                      <td className="py-3.5 px-4">
                        <Badge variant="outline" className="normal-case">
                          {st.level}
                        </Badge>
                      </td>

                      {/* Current Syllabus Progress */}
                      <td className="py-3.5 px-4 min-w-[160px]">
                        <div className="space-y-1">
                          <div className="flex justify-between text-[11px] font-mono">
                            <span className="font-bold text-[var(--gv-ink)]">
                              {progress.completedTopicsCount}/{progress.totalTopicsCount} Topics
                            </span>
                            <span className="font-extrabold text-[var(--gv-brand)]">{progress.progressPct}%</span>
                          </div>
                          <div className="w-full bg-stone-200 h-2 rounded-full overflow-hidden">
                            <div
                              className="bg-[var(--gv-brand)] h-full rounded-full transition-all"
                              style={{ width: `${progress.progressPct}%` }}
                            />
                          </div>
                        </div>
                      </td>

                      {/* Current Status */}
                      <td className="py-3.5 px-4">
                        <Badge
                          variant={getStatusBadgeVariant(progress.currentStatus)}
                          className="inline-flex items-center gap-1 normal-case"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-current" />
                          {progress.currentStatus}
                        </Badge>
                      </td>

                      {/* Last Activity Date */}
                      <td className="py-3.5 px-4 text-[var(--gv-ink-muted)] font-mono text-[11px]">
                        {progress.lastActivity}
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <Button
                          size="sm"
                          leftIcon={Eye}
                          onClick={() => setSelectedStudent(st)}
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* READ-ONLY STUDENT PROGRESS DETAILS MODAL */}
      {selectedStudent && (
        <div className="fixed inset-0 z-[9990] bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          {(() => {
            const data = getStudentProgressData(selectedStudent);

            return (
              <div className="admin-card max-w-4xl w-full p-6 shadow-2xl space-y-6 text-[var(--gv-ink)] my-8 max-h-[90vh] overflow-y-auto">
                {/* Modal Header */}
                <div className="flex items-center justify-between pb-4 border-b border-[var(--gv-border)]">
                  <div className="flex items-center gap-4">
                    <img
                      src={selectedStudent.photoUrl}
                      alt={selectedStudent.firstName}
                      className="w-14 h-14 rounded-2xl object-cover border-2 border-[var(--gv-brand)] shadow-xs"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold text-[var(--gv-ink)]">
                          {selectedStudent.firstName} {selectedStudent.surname}
                        </h2>
                        <Badge variant="soft" className="font-mono normal-case">
                          {selectedStudent.id}
                        </Badge>
                      </div>
                      <p className="text-xs text-[var(--gv-ink-muted)] mt-0.5">
                        {selectedStudent.level} • {selectedStudent.batch}
                      </p>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    leftIcon={X}
                    aria-label="Close"
                    onClick={() => setSelectedStudent(null)}
                  />
                </div>

                {/* Read-Only Monitoring Banner */}
                <div className="bg-[var(--gv-surface)] border border-[var(--gv-border)] p-3 rounded-xl flex items-center justify-between gap-3 text-xs">
                  <div className="flex items-center gap-2 text-[var(--gv-ink-muted)]">
                    <ShieldCheck className="w-4 h-4 text-[var(--gv-brand)]" />
                    <span>
                      <strong>Monitoring View:</strong> Student review and promotion decisions are managed by Guruji <strong>{data.assignedTeacherName}</strong>.
                    </span>
                  </div>
                  <span className="text-[11px] font-mono text-[var(--gv-ink-muted)]">Last Synced: Today</span>
                </div>

                {/* 1. STUDENT & ACADEMIC INFORMATION SECTIONS */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Student Information */}
                  <div className="bg-[var(--gv-surface)] p-4 rounded-xl border border-[var(--gv-border)]/80 space-y-2">
                    <h3 className="text-xs font-bold text-[var(--gv-brand)] uppercase tracking-wider block">
                      Student Information
                    </h3>
                    <div className="text-xs space-y-1.5">
                      <div className="flex items-center justify-between text-[var(--gv-ink-muted)]">
                        <span>Gender:</span>
                        <strong className="text-[var(--gv-ink)]">{selectedStudent.gender}</strong>
                      </div>
                      <div className="flex items-center justify-between text-[var(--gv-ink-muted)]">
                        <span>DOB:</span>
                        <strong className="text-[var(--gv-ink)] font-mono">{selectedStudent.dob}</strong>
                      </div>
                      <div className="flex items-center justify-between text-[var(--gv-ink-muted)]">
                        <span>Student Mobile:</span>
                        <strong className="text-[var(--gv-ink)] font-mono">{selectedStudent.mobile}</strong>
                      </div>
                      <div className="flex items-center justify-between text-[var(--gv-ink-muted)]">
                        <span>Parent Mobile:</span>
                        <strong className="text-[var(--gv-ink)] font-mono">{selectedStudent.parentMobile}</strong>
                      </div>
                      <div className="flex items-center justify-between text-[var(--gv-ink-muted)]">
                        <span>Email:</span>
                        <strong className="text-[var(--gv-ink)] text-[11px] truncate">{selectedStudent.email}</strong>
                      </div>
                      <div className="text-[var(--gv-ink-muted)] pt-1 border-t border-[var(--gv-border)] text-[11px]">
                        <span>Address:</span>
                        <p className="text-[var(--gv-ink)] font-medium line-clamp-2">{selectedStudent.address}</p>
                      </div>
                    </div>
                  </div>

                  {/* Academic Information */}
                  <div className="bg-[var(--gv-surface)] p-4 rounded-xl border border-[var(--gv-border)]/80 space-y-2">
                    <h3 className="text-xs font-bold text-[var(--gv-brand)] uppercase tracking-wider block">
                      Academic Information
                    </h3>
                    <div className="text-xs space-y-1.5">
                      <div className="flex items-center justify-between text-[var(--gv-ink-muted)]">
                        <span>Current Level:</span>
                        <strong className="text-[var(--gv-ink)]">{selectedStudent.level}</strong>
                      </div>
                      <div className="flex items-center justify-between text-[var(--gv-ink-muted)]">
                        <span>Batch Slot:</span>
                        <strong className="text-[var(--gv-ink)]">{selectedStudent.batch}</strong>
                      </div>
                      <div className="flex items-center justify-between text-[var(--gv-ink-muted)]">
                        <span>Registration Date:</span>
                        <strong className="text-[var(--gv-ink)] font-mono">{selectedStudent.registrationDate}</strong>
                      </div>
                      <div className="flex items-center justify-between text-[var(--gv-ink-muted)] pt-1 border-t border-[var(--gv-border)]">
                        <span>Attendance Rate:</span>
                        <strong className="text-emerald-700 font-mono font-bold">{selectedStudent.attendanceRate}%</strong>
                      </div>
                      <div className="flex items-center justify-between text-[var(--gv-ink-muted)]">
                        <span>Account Status:</span>
                        <Badge variant="success" className="normal-case">
                          {selectedStudent.status}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Assigned Teacher */}
                  <div className="bg-[var(--gv-surface)] p-4 rounded-xl border border-[var(--gv-border)]/80 space-y-2">
                    <h3 className="text-xs font-bold text-[var(--gv-brand)] uppercase tracking-wider block">
                      Assigned Teacher
                    </h3>
                    <div className="text-xs space-y-2">
                      <div className="p-3 bg-white rounded-lg border border-[var(--gv-border)] flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[var(--gv-brand-soft)] text-[var(--gv-brand)] flex items-center justify-center font-bold text-xs">
                          {data.assignedTeacherName.charAt(0)}
                        </div>
                        <div>
                          <div className="font-bold text-[var(--gv-ink)]">{data.assignedTeacherName}</div>
                          <span className="text-[10px] text-[var(--gv-ink-muted)] block">Pathshala Senior Guruji</span>
                        </div>
                      </div>
                      <div className="text-[var(--gv-ink-muted)] text-[11px] leading-relaxed">
                        Responsible for evaluating student recitations, oral tests, and issuing level promotions.
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. PROGRESS SECTION */}
                <div className="bg-[var(--gv-surface)] p-5 rounded-2xl border border-[var(--gv-border)] space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <h3 className="text-xs font-bold text-[var(--gv-brand)] uppercase tracking-wider">Progress Overview</h3>
                      <p className="text-xs text-[var(--gv-ink-muted)] mt-0.5">Syllabus topics completed out of assigned level curriculum</p>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <span className="text-[10px] text-[var(--gv-ink-muted)] font-bold uppercase block">Completion Rate</span>
                        <span className="text-lg font-bold text-[var(--gv-brand)]">{data.progressPct}%</span>
                      </div>
                      <div className="w-12 h-12 rounded-full border-4 border-stone-200 border-t-[var(--gv-brand)] flex items-center justify-center font-bold text-xs text-[var(--gv-brand)]">
                        {data.completedTopicsCount}/{data.totalTopicsCount}
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-stone-200 h-3 rounded-full overflow-hidden">
                    <div
                      className="bg-[var(--gv-brand)] h-full rounded-full transition-all"
                      style={{ width: `${data.progressPct}%` }}
                    />
                  </div>

                  {/* Completed vs Pending Topics Breakdown */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    {/* Completed Topics */}
                    <div className="bg-white p-3.5 rounded-xl border border-[var(--gv-border)] space-y-2">
                      <div className="flex items-center justify-between border-b border-stone-100 pb-2">
                        <span className="text-xs font-bold text-emerald-800 flex items-center gap-1.5">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          <span>Completed Topics ({data.completedTopics.length})</span>
                        </span>
                        <Badge variant="success" className="normal-case">
                          VERIFIED
                        </Badge>
                      </div>
                      <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                        {data.completedTopics.length === 0 ? (
                          <span className="text-xs text-[var(--gv-ink-muted)] italic">No completed topics yet.</span>
                        ) : (
                          data.completedTopics.map((topic, i) => (
                            <div key={topic.id || i} className="flex items-center justify-between text-xs py-1 border-b border-stone-50">
                              <span className="font-semibold text-[var(--gv-ink)]">{i + 1}. {topic.chapterName}</span>
                              <span className="text-[10px] font-mono text-emerald-700 font-bold">✓ Passed</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Pending Topics */}
                    <div className="bg-white p-3.5 rounded-xl border border-[var(--gv-border)] space-y-2">
                      <div className="flex items-center justify-between border-b border-stone-100 pb-2">
                        <span className="text-xs font-bold text-[var(--gv-ink)] flex items-center gap-1.5">
                          <Clock className="w-4 h-4 text-amber-600" />
                          <span>Pending Topics ({data.pendingTopics.length})</span>
                        </span>
                        <Badge variant="warning" className="normal-case">
                          IN PROGRESS
                        </Badge>
                      </div>
                      <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                        {data.pendingTopics.length === 0 ? (
                          <span className="text-xs text-emerald-700 font-bold">All level topics completed! 🎉</span>
                        ) : (
                          data.pendingTopics.map((topic, i) => (
                            <div key={topic.id || i} className="flex items-center justify-between text-xs py-1 border-b border-stone-50">
                              <span className="font-medium text-[var(--gv-ink-muted)]">{data.completedTopics.length + i + 1}. {topic.chapterName}</span>
                              <span className="text-[10px] font-mono text-[var(--gv-ink-muted)]">Upcoming</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3. Status Matrix */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div className="bg-[var(--gv-surface)] p-3 rounded-xl border border-[var(--gv-border)]">
                    <span className="text-[10px] font-mono text-[var(--gv-ink-muted)] font-bold uppercase block">Current Submission</span>
                    <span className="font-bold text-[var(--gv-ink)] block mt-1">{data.currentSubmissionStatus}</span>
                  </div>

                  <div className="bg-[var(--gv-surface)] p-3 rounded-xl border border-[var(--gv-border)]">
                    <span className="text-[10px] font-mono text-[var(--gv-ink-muted)] font-bold uppercase block">Teacher Review Status</span>
                    <span className="font-bold text-amber-800 block mt-1">{data.teacherReviewStatus}</span>
                  </div>

                  <div className="bg-[var(--gv-surface)] p-3 rounded-xl border border-[var(--gv-border)]">
                    <span className="text-[10px] font-mono text-[var(--gv-ink-muted)] font-bold uppercase block">Final Revision Status</span>
                    <span className="font-bold text-[var(--gv-ink)] block mt-1">{data.finalRevisionStatus}</span>
                  </div>

                  <div className="bg-[var(--gv-surface)] p-3 rounded-xl border border-[var(--gv-border)]">
                    <span className="text-[10px] font-mono text-[var(--gv-ink-muted)] font-bold uppercase block">Promotion Status</span>
                    <span className="font-bold text-emerald-700 block mt-1">{data.promotionStatus}</span>
                  </div>
                </div>

                {/* 4. Clean Learning Journey Timeline */}
                <div className="bg-white p-5 rounded-2xl border border-[var(--gv-border)] space-y-4">
                  <h3 className="text-sm font-bold text-[var(--gv-ink)]">Learning Journey Timeline</h3>

                  <div className="relative">
                    {/* Horizontal Step Flow */}
                    <div className="grid grid-cols-2 md:grid-cols-7 gap-2 text-center">
                      {[
                        { label: 'Registration', done: true, current: false },
                        { label: 'Level Assigned', done: true, current: false },
                        { label: 'Learning in Progress', done: true, current: data.currentStatus === 'Learning in Progress' },
                        { label: 'Teacher Review', done: data.progressPct >= 75, current: data.currentStatus === 'Teacher Review' },
                        { label: 'Final Revision', done: data.progressPct >= 85, current: data.currentStatus === 'Final Revision' },
                        { label: 'Approved', done: data.progressPct >= 95, current: data.currentStatus === 'Approved' },
                        { label: 'Promoted', done: data.progressPct >= 100, current: data.currentStatus === 'Promoted' },
                      ].map((step, idx) => (
                        <div
                          key={step.label}
                          className={`p-3 rounded-xl border flex flex-col items-center justify-between space-y-2 transition-all ${
                            step.done
                              ? 'bg-emerald-50/70 border-emerald-200 text-emerald-900'
                              : step.current
                              ? 'bg-amber-50 border-amber-300 text-amber-900 ring-2 ring-amber-200'
                              : 'bg-[var(--gv-surface)] border-[var(--gv-border)] text-[var(--gv-ink-muted)]'
                          }`}
                        >
                          <div className="text-[10px] font-mono font-bold text-[var(--gv-ink-muted)] uppercase">
                            Step {idx + 1}
                          </div>

                          <div
                            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                              step.done
                                ? 'bg-emerald-600 text-white'
                                : step.current
                                ? 'bg-amber-500 text-white'
                                : 'bg-stone-200 text-[var(--gv-ink-muted)]'
                            }`}
                          >
                            {step.done ? '✓' : idx + 1}
                          </div>

                          <div className="text-[11px] font-bold leading-tight">{step.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Footer Action */}
                <div className="pt-3 border-t border-[var(--gv-border)] flex justify-end">
                  <Button variant="secondary" onClick={() => setSelectedStudent(null)}>
                    Close Window
                  </Button>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};
